import { logger } from '../loggingService';
import { toast } from '../../lib/toast';
import { supabase } from '../../lib/supabase';

interface APIRequestConfig {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  endpoint: string;
  body?: any;
  headers?: Record<string, string>;
  params?: Record<string, any>;
  timeout?: number;
  retries?: number;
  cache?: {
    enabled: boolean;
    ttl?: number; // Time to live in milliseconds
    key?: string;
  };
  showError?: boolean;
  requiresAuth?: boolean;
}

interface APIResponse<T = any> {
  data?: T;
  error?: string;
  status: number;
  cached?: boolean;
  retryCount?: number;
}

interface CacheEntry {
  data: any;
  timestamp: number;
  ttl: number;
}

/**
 * Centralized API Client with retry logic, caching, and error handling
 */
export class APIClient {
  private static cache = new Map<string, CacheEntry>();
  private static pendingRequests = new Map<string, Promise<any>>();
  private static requestQueue: Array<() => Promise<void>> = [];
  private static isProcessingQueue = false;
  private static maxConcurrentRequests = 5;
  private static activeRequests = 0;

  /**
   * Make an API request with automatic retry and caching
   */
  static async request<T = any>(config: APIRequestConfig): Promise<APIResponse<T>> {
    const {
      method,
      endpoint,
      body,
      headers = {},
      params,
      timeout = 30000,
      retries = 3,
      cache = { enabled: false },
      showError = true,
      requiresAuth = true
    } = config;

    // Check authentication if required
    if (requiresAuth) {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        const error = 'Authentication required';
        if (showError) {
          toast.error('Please sign in to continue');
        }
        return { error, status: 401 };
      }
      
      // Add auth header
      headers['Authorization'] = `Bearer ${session.access_token}`;
    }

    // Generate cache key
    const cacheKey = cache.key || this.generateCacheKey(method, endpoint, params, body);

    // Check cache for GET requests
    if (method === 'GET' && cache.enabled) {
      const cachedData = this.getFromCache(cacheKey, cache.ttl);
      if (cachedData) {
        logger.debug('Returning cached data', { endpoint, cacheKey });
        return {
          data: cachedData,
          status: 200,
          cached: true
        };
      }
    }

    // Check for pending request (request deduplication)
    const pendingKey = `${method}:${endpoint}:${JSON.stringify(params)}`;
    if (this.pendingRequests.has(pendingKey)) {
      logger.debug('Waiting for pending request', { endpoint });
      try {
        const data = await this.pendingRequests.get(pendingKey);
        return { data, status: 200 };
      } catch (error) {
        return { 
          error: error instanceof Error ? error.message : 'Request failed', 
          status: 500 
        };
      }
    }

    // Create the request promise
    const requestPromise = this.executeRequest<T>(
      method,
      endpoint,
      body,
      headers,
      params,
      timeout,
      retries,
      showError
    );

    // Store as pending request
    this.pendingRequests.set(pendingKey, requestPromise);

    try {
      const response = await requestPromise;
      
      // Cache successful GET requests
      if (method === 'GET' && cache.enabled && response.data) {
        this.saveToCache(cacheKey, response.data, cache.ttl || 300000); // Default 5 minutes
      }

      return response;
    } finally {
      // Clean up pending request
      this.pendingRequests.delete(pendingKey);
    }
  }

  /**
   * Execute the actual request with retry logic
   */
  private static async executeRequest<T>(
    method: string,
    endpoint: string,
    body: any,
    headers: Record<string, string>,
    params: any,
    timeout: number,
    maxRetries: number,
    showError: boolean
  ): Promise<APIResponse<T>> {
    let lastError: string | undefined;
    let retryCount = 0;

    // Wait in queue if too many concurrent requests
    await this.waitInQueue();

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        this.activeRequests++;
        
        // Build URL with params
        const url = this.buildUrl(endpoint, params);
        
        // Create abort controller for timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);

        // Make the request
        const startTime = Date.now();
        const response = await fetch(url, {
          method,
          headers: {
            'Content-Type': 'application/json',
            ...headers
          },
          body: body ? JSON.stringify(body) : undefined,
          signal: controller.signal
        });

        clearTimeout(timeoutId);
        
        const duration = Date.now() - startTime;
        
        // Log request details
        logger.debug('API request completed', {
          method,
          endpoint,
          status: response.status,
          duration,
          attempt: attempt + 1
        });

        // Handle response
        if (response.ok) {
          let data: T;
          const contentType = response.headers.get('content-type');
          
          if (contentType?.includes('application/json')) {
            data = await response.json();
          } else {
            data = await response.text() as any;
          }

          return {
            data,
            status: response.status,
            retryCount
          };
        }

        // Handle error responses
        let errorMessage = `Request failed with status ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch {
          // If response is not JSON, use status text
          errorMessage = response.statusText || errorMessage;
        }

        // Don't retry on client errors (4xx)
        if (response.status >= 400 && response.status < 500) {
          if (showError) {
            this.showErrorMessage(response.status, errorMessage);
          }
          return {
            error: errorMessage,
            status: response.status,
            retryCount
          };
        }

        lastError = errorMessage;
        retryCount++;

        // Wait before retrying (exponential backoff)
        if (attempt < maxRetries - 1) {
          const delay = Math.min(1000 * Math.pow(2, attempt), 10000);
          logger.debug(`Retrying request in ${delay}ms`, { 
            endpoint, 
            attempt: attempt + 1,
            maxRetries 
          });
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      } catch (error) {
        if (error instanceof Error) {
          if (error.name === 'AbortError') {
            lastError = 'Request timeout';
          } else {
            lastError = error.message;
          }
        } else {
          lastError = 'Unknown error occurred';
        }

        retryCount++;
        
        // Wait before retrying
        if (attempt < maxRetries - 1) {
          const delay = Math.min(1000 * Math.pow(2, attempt), 10000);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      } finally {
        this.activeRequests--;
        this.processQueue();
      }
    }

    // All retries failed
    logger.error('API request failed after retries', {
      method,
      endpoint,
      error: lastError,
      retries: retryCount
    });

    if (showError) {
      toast.error(lastError || 'Request failed. Please try again.');
    }

    return {
      error: lastError || 'Request failed',
      status: 500,
      retryCount
    };
  }

  /**
   * Queue management for rate limiting
   */
  private static async waitInQueue(): Promise<void> {
    if (this.activeRequests >= this.maxConcurrentRequests) {
      return new Promise(resolve => {
        this.requestQueue.push(resolve);
      });
    }
  }

  private static processQueue(): void {
    if (this.isProcessingQueue) return;
    this.isProcessingQueue = true;

    while (this.requestQueue.length > 0 && this.activeRequests < this.maxConcurrentRequests) {
      const resolve = this.requestQueue.shift();
      if (resolve) {
        resolve();
      }
    }

    this.isProcessingQueue = false;
  }

  /**
   * Build URL with query parameters
   */
  private static buildUrl(endpoint: string, params?: Record<string, any>): string {
    // Handle both relative and absolute URLs
    let url: string;
    if (endpoint.startsWith('http://') || endpoint.startsWith('https://')) {
      url = endpoint;
    } else {
      // Use Supabase URL as base
      const baseUrl = import.meta.env.VITE_SUPABASE_URL;
      url = `${baseUrl}/functions/v1${endpoint}`;
    }

    if (params && Object.keys(params).length > 0) {
      const queryString = new URLSearchParams(
        Object.entries(params).reduce((acc, [key, value]) => {
          if (value !== undefined && value !== null) {
            acc[key] = String(value);
          }
          return acc;
        }, {} as Record<string, string>)
      ).toString();
      
      url += (url.includes('?') ? '&' : '?') + queryString;
    }

    return url;
  }

  /**
   * Show appropriate error message to user
   */
  private static showErrorMessage(status: number, message: string): void {
    switch (status) {
      case 401:
        toast.error('Your session has expired. Please sign in again.');
        break;
      case 403:
        toast.error('You do not have permission to perform this action.');
        break;
      case 404:
        toast.error('The requested resource was not found.');
        break;
      case 429:
        toast.error('Too many requests. Please wait a moment and try again.');
        break;
      case 500:
      case 502:
      case 503:
        toast.error('Server error. Our team has been notified.');
        break;
      default:
        toast.error(message || 'An error occurred. Please try again.');
    }
  }

  /**
   * Cache management
   */
  private static generateCacheKey(
    method: string,
    endpoint: string,
    params?: any,
    body?: any
  ): string {
    return `${method}:${endpoint}:${JSON.stringify(params || {})}:${JSON.stringify(body || {})}`;
  }

  private static getFromCache(key: string, ttl?: number): any | null {
    const entry = this.cache.get(key);
    if (!entry) return null;

    const now = Date.now();
    const age = now - entry.timestamp;

    // Check if cache is still valid
    if (age <= entry.ttl) {
      return entry.data;
    }

    // Cache expired
    this.cache.delete(key);
    return null;
  }

  private static saveToCache(key: string, data: any, ttl: number): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl
    });

    // Limit cache size (LRU)
    if (this.cache.size > 100) {
      const firstKey = this.cache.keys().next().value;
      if (firstKey) {
        this.cache.delete(firstKey);
      }
    }
  }

  /**
   * Clear cache
   */
  static clearCache(pattern?: string): void {
    if (pattern) {
      // Clear specific pattern
      const keys = Array.from(this.cache.keys());
      keys.forEach(key => {
        if (key.includes(pattern)) {
          this.cache.delete(key);
        }
      });
    } else {
      // Clear all
      this.cache.clear();
    }
    
    logger.debug('Cache cleared', { pattern });
  }

  /**
   * Get cache statistics
   */
  static getCacheStats(): {
    size: number;
    entries: Array<{ key: string; age: number; ttl: number }>;
  } {
    const now = Date.now();
    const entries = Array.from(this.cache.entries()).map(([key, entry]) => ({
      key,
      age: now - entry.timestamp,
      ttl: entry.ttl
    }));

    return {
      size: this.cache.size,
      entries
    };
  }

  /**
   * Abort all pending requests
   */
  static abortAll(): void {
    this.pendingRequests.clear();
    this.requestQueue = [];
    logger.info('All pending requests aborted');
  }
}

// Convenience methods for common HTTP methods
export const api = {
  get: <T = any>(endpoint: string, options?: Partial<APIRequestConfig>) =>
    APIClient.request<T>({ method: 'GET', endpoint, ...options }),
    
  post: <T = any>(endpoint: string, body?: any, options?: Partial<APIRequestConfig>) =>
    APIClient.request<T>({ method: 'POST', endpoint, body, ...options }),
    
  put: <T = any>(endpoint: string, body?: any, options?: Partial<APIRequestConfig>) =>
    APIClient.request<T>({ method: 'PUT', endpoint, body, ...options }),
    
  delete: <T = any>(endpoint: string, options?: Partial<APIRequestConfig>) =>
    APIClient.request<T>({ method: 'DELETE', endpoint, ...options }),
    
  patch: <T = any>(endpoint: string, body?: any, options?: Partial<APIRequestConfig>) =>
    APIClient.request<T>({ method: 'PATCH', endpoint, body, ...options }),
};
